import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Shield, Users, Crown, ArrowLeft, RefreshCw, CheckCircle, XCircle, FileText, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = ({ onBack }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(data.users || []);
      setTotal(data.total || 0);
      setMonth(data.month || "");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const togglePremium = async (userId) => {
    setTogglingId(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/toggle-premium`, {}, { withCredentials: true });
      fetchUsers();
    } catch { /* ignore */ }
    finally { setTogglingId(null); }
  };

  if (!user || user.role !== "admin") {
    return null;
  }

  const premiumCount = users.filter(u => u.is_premium).length;
  const totalValidationsMonth = users.reduce((sum, u) => sum + (u.validations_this_month || 0), 0);
  const totalValidationsAll = users.reduce((sum, u) => sum + (u.validations_total || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="text-slate-500 hover:text-slate-700 transition-colors" data-testid="admin-back-button">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="bg-gradient-to-br from-purple-600 to-indigo-600 p-2 rounded-xl shadow-lg">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Administration</h1>
              <p className="text-xs text-slate-500">Gestion des utilisateurs</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchUsers} disabled={loading} className="gap-2" data-testid="admin-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button 
            variant="default" 
            size="sm" 
            onClick={() => navigate('/admin/validations')} 
            className="gap-2 ml-2 bg-gradient-to-r from-purple-600 to-indigo-600"
            data-testid="admin-validations-link"
          >
            <History className="h-4 w-4" />
            Historique des validations
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-6 text-sm" data-testid="admin-error">
            {error}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="shadow-md" data-testid="stat-total-users">
            <CardContent className="p-5 text-center">
              <Users className="h-6 w-6 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{total}</p>
              <p className="text-xs text-slate-500">Utilisateurs</p>
            </CardContent>
          </Card>
          <Card className="shadow-md" data-testid="stat-premium-users">
            <CardContent className="p-5 text-center">
              <Crown className="h-6 w-6 text-amber-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{premiumCount}</p>
              <p className="text-xs text-slate-500">Supporters</p>
            </CardContent>
          </Card>
          <Card className="shadow-md" data-testid="stat-month-validations">
            <CardContent className="p-5 text-center">
              <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{totalValidationsMonth}</p>
              <p className="text-xs text-slate-500">Valid. ce mois</p>
            </CardContent>
          </Card>
          <Card className="shadow-md" data-testid="stat-total-validations">
            <CardContent className="p-5 text-center">
              <CheckCircle className="h-6 w-6 text-indigo-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{totalValidationsAll}</p>
              <p className="text-xs text-slate-500">Valid. totales</p>
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card className="shadow-xl" data-testid="admin-users-table">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-800">
              <Users className="h-5 w-5" />
              Utilisateurs ({total})
              {month && <Badge variant="outline" className="ml-2 text-xs font-normal">{month}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading && users.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <div className="h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                Chargement...
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12 text-slate-400">Aucun utilisateur</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-3 font-semibold text-slate-600">Email</th>
                      <th className="text-left py-3 px-3 font-semibold text-slate-600">Nom</th>
                      <th className="text-center py-3 px-3 font-semibold text-slate-600">Connexion</th>
                      <th className="text-center py-3 px-3 font-semibold text-slate-600">Statut</th>
                      <th className="text-center py-3 px-3 font-semibold text-slate-600">Ce jour</th>
                      <th className="text-center py-3 px-3 font-semibold text-slate-600">Ce mois</th>
                      <th className="text-center py-3 px-3 font-semibold text-slate-600">Total</th>
                      <th className="text-center py-3 px-3 font-semibold text-slate-600">Inscription</th>
                      <th className="text-center py-3 px-3 font-semibold text-slate-600">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`user-row-${u.user_id}`}>
                        <td className="py-3 px-3">
                          <span className="font-medium text-slate-800">{u.email}</span>
                          {u.role === "admin" && (
                            <Badge className="ml-2 bg-purple-100 text-purple-700 border-purple-200 text-[10px]">Admin</Badge>
                          )}
                        </td>
                        <td className="py-3 px-3 text-slate-600">{u.name || "—"}</td>
                        <td className="py-3 px-3 text-center">
                          <Badge variant="outline" className="text-xs">
                            {u.auth_provider === "google" ? "Google" : "Email"}
                          </Badge>
                        </td>
                        <td className="py-3 px-3 text-center">
                          {u.is_premium ? (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">Supporter</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs text-slate-500">Free</Badge>
                          )}
                        </td>
                        <td className="py-3 px-3 text-center font-mono text-slate-700">{u.validations_today || 0}</td>
                        <td className="py-3 px-3 text-center font-mono text-slate-700">{u.validations_this_month}</td>
                        <td className="py-3 px-3 text-center font-mono text-slate-700">{u.validations_total}</td>
                        <td className="py-3 px-3 text-center text-xs text-slate-500">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}
                        </td>
                        <td className="py-3 px-3 text-center">
                          {u.role !== "admin" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => togglePremium(u.user_id)}
                              disabled={togglingId === u.user_id}
                              data-testid={`toggle-premium-${u.user_id}`}
                              className={`text-xs ${u.is_premium ? 'text-red-600 hover:text-red-700 hover:bg-red-50' : 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'}`}
                            >
                              {togglingId === u.user_id ? "..." : u.is_premium ? (
                                <><XCircle className="h-3 w-3 mr-1" /> Retirer</>
                              ) : (
                                <><Crown className="h-3 w-3 mr-1" /> Promouvoir</>
                              )}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminPage;
